import React, { useMemo, useRef, useEffect, useState } from 'react';
import { AgCharts } from 'ag-charts-react';
import { getAgChartTheme, getChartColors } from '../../../analytics/charts/chartTheme';
import { useTheme } from '../../../../stores/ThemeContext';
import { Target, TrendingUp, TrendingDown } from 'lucide-react';

const fmt = (v) => {
  if (v == null || isNaN(v)) return '—';
  return v >= 0 ? `+$${Math.abs(v).toFixed(2)}` : `-$${Math.abs(v).toFixed(2)}`;
};

const PlaybookFitScore = ({ data, loading }) => {
  const { isDark } = useTheme();
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 200 });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        if (width > 0) setDimensions({ width, height: 200 });
      }
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, []);

  const colors = useMemo(() => getChartColors(isDark), [isDark]);

  const { high_fit, low_fit, distribution = [], threshold = 80 } = data || {};

  const options = useMemo(() => ({
    theme: getAgChartTheme(isDark),
    width: dimensions.width || undefined,
    height: dimensions.height,
    data: distribution.filter((d) => d.count > 0),
    series: [
      {
        type: 'bar',
        xKey: 'band',
        yKey: 'count',
        yName: 'Trades',
        itemStyler: ({ datum }) => {
          const lo = parseInt(datum.band, 10);
          return {
            fill: lo >= threshold ? colors.win : colors.loss,
            stroke: lo >= threshold ? colors.win : colors.loss,
          };
        },
        tooltip: {
          renderer: ({ datum }) => ({
            title: `${datum.band} score`,
            content: `${datum.count} trade${datum.count !== 1 ? 's' : ''}\nAvg PnL: ${fmt(datum.avg_pnl)}`,
          }),
        },
      },
    ],
    axes: [
      {
        type: 'category',
        position: 'bottom',
        label: { color: isDark ? '#a1a1aa' : '#6b7280', fontSize: 9 },
        title: { text: 'Discipline Score Band', color: isDark ? '#a1a1aa' : '#6b7280', fontSize: 10 },
      },
      {
        type: 'number',
        position: 'left',
        label: { color: isDark ? '#a1a1aa' : '#6b7280', fontSize: 10 },
        title: { text: 'Trades', color: isDark ? '#a1a1aa' : '#6b7280', fontSize: 10 },
      },
    ],
    legend: { enabled: false },
  }), [distribution, dimensions, isDark, colors, threshold]);

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-4 animate-pulse">
        <div className="h-4 bg-muted rounded w-1/3 mb-4" />
        <div className="h-48 bg-muted rounded mb-4" />
        <div className="grid grid-cols-2 gap-3">
          <div className="h-20 bg-muted rounded" />
          <div className="h-20 bg-muted rounded" />
        </div>
      </div>
    );
  }

  const noData = !data || (high_fit?.count === 0 && low_fit?.count === 0);

  return (
    <div className="bg-card rounded-lg border border-border p-4">
      <div className="flex items-center gap-2 mb-1">
        <Target className="w-4 h-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold text-muted-foreground">Playbook Fit Score</h3>
      </div>
      <p className="text-xs text-muted-foreground/60 mb-4">
        High-fit (≥{threshold}% rules followed) vs low-fit trades
      </p>

      {noData ? (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <Target className="w-8 h-8 text-muted-foreground/30 mb-2" />
          <p className="text-sm text-muted-foreground">No data yet.</p>
        </div>
      ) : (
        <>
          {/* Distribution bar chart */}
          <div ref={containerRef} className="w-full mb-4">
            {dimensions.width > 0 && <AgCharts options={options} />}
          </div>

          {/* High vs low comparison cards */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-green-500/20 bg-green-500/5 p-3">
              <div className="flex items-center gap-1 mb-2">
                <TrendingUp className="w-3 h-3 text-green-400" />
                <span className="text-xs font-semibold text-green-400">High Fit</span>
                <span className="text-[10px] text-muted-foreground ml-auto">≥{threshold}%</span>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between text-[10px]">
                  <span className="text-muted-foreground">Trades</span>
                  <span className="text-foreground font-medium">{high_fit?.count ?? 0}</span>
                </div>
                <div className="flex justify-between text-[10px]">
                  <span className="text-muted-foreground">Avg PnL</span>
                  <span className={`font-semibold tabular-nums ${(high_fit?.avg_pnl ?? 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {fmt(high_fit?.avg_pnl)}
                  </span>
                </div>
                <div className="flex justify-between text-[10px]">
                  <span className="text-muted-foreground">Win Rate</span>
                  <span className={`font-semibold ${(high_fit?.win_rate ?? 0) >= 50 ? 'text-green-400' : 'text-red-400'}`}>
                    {(high_fit?.win_rate ?? 0).toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-red-500/20 bg-red-500/5 p-3">
              <div className="flex items-center gap-1 mb-2">
                <TrendingDown className="w-3 h-3 text-red-400" />
                <span className="text-xs font-semibold text-red-400">Low Fit</span>
                <span className="text-[10px] text-muted-foreground ml-auto">&lt;{threshold}%</span>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between text-[10px]">
                  <span className="text-muted-foreground">Trades</span>
                  <span className="text-foreground font-medium">{low_fit?.count ?? 0}</span>
                </div>
                <div className="flex justify-between text-[10px]">
                  <span className="text-muted-foreground">Avg PnL</span>
                  <span className={`font-semibold tabular-nums ${(low_fit?.avg_pnl ?? 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {fmt(low_fit?.avg_pnl)}
                  </span>
                </div>
                <div className="flex justify-between text-[10px]">
                  <span className="text-muted-foreground">Win Rate</span>
                  <span className={`font-semibold ${(low_fit?.win_rate ?? 0) >= 50 ? 'text-green-400' : 'text-red-400'}`}>
                    {(low_fit?.win_rate ?? 0).toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PlaybookFitScore;
